// Self-install activation (task 6, phase 2): /activate/<QR token> or /activate. One step per
// screen, phone first: tracker → install guide → account → vehicle → confirm → waiting for
// signal → done. The ticket from step 1 is kept in sessionStorage so a reload carries on;
// passwords never are.
import { FormEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Link } from '../components/Links';
import { activate, ActivateConfig, ActivateError, Identified, NewAccount, NewVehicle, Signal, VehicleType } from '../api';
import { useAuth } from '../auth';
import { FleetMap } from '../components/FleetMap';
import { GuideContent, PublicFrame } from './Public';

type Step = 'identify' | 'guide' | 'account' | 'vehicle' | 'confirm' | 'signal' | 'done';
const NUMBERED: Step[] = ['identify', 'guide', 'account', 'vehicle', 'confirm'];
const STEP_LABEL: Record<Step, string> = {
  identify: 'Your tracker', guide: 'Fit it', account: 'Your account', vehicle: 'Your vehicle', confirm: 'Activate',
  signal: 'Waiting for signal', done: 'Done',
};

interface Saved {
  step: Step;
  found: Identified | null;
  vehicle: NewVehicle | null;
  done: { vehicleId: number; expiresAt: string } | null;
}

const STORE = 'ff.activate';
const EMPTY: Saved = { step: 'identify', found: null, vehicle: null, done: null };
function load(): Saved {
  try { return { ...EMPTY, ...JSON.parse(sessionStorage.getItem(STORE) ?? '{}') }; } catch { return EMPTY; }
}
function save(s: Saved) {
  try { sessionStorage.setItem(STORE, JSON.stringify(s)); } catch { /* private mode: no reload recovery */ }
}
function forget() {
  try { sessionStorage.removeItem(STORE); } catch { /* nothing */ }
}

const TYPES: Array<[VehicleType, string]> = [
  ['car', 'Car'], ['van', 'Van'], ['truck', 'Truck'], ['trailer', 'Trailer'], ['tractor', 'Tractor'], ['plant', 'Plant / machine'], ['other', 'Other'],
];
const longDate = (iso: string) => new Date(iso).toLocaleDateString('en-IE', { day: 'numeric', month: 'long', year: 'numeric' });

export function Activate() {
  const { token } = useParams();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState<ActivateConfig | null>(null);
  const [state, setState] = useState<Saved>(load);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState<{ message: string; code?: string } | null>(null);
  // The new account's details, including the password, stay in memory only.
  const [account, setAccount] = useState<NewAccount | null>(null);
  const started = useRef(false);

  const go = (patch: Partial<Saved>) => setState((s) => { const n = { ...s, ...patch }; save(n); return n; });
  const fail = (e: unknown) => setError({ message: (e as Error).message, code: e instanceof ActivateError ? e.code : undefined });

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        const c = await activate.config();
        setConfig(c);
        if (!c.enabled) return;
        if (token) {
          // Take the token out of the address bar and history straight away.
          history.replaceState(null, '', '/activate');
          forget();
          const found = await activate.byToken(token);
          const n = { ...EMPTY, found, step: 'guide' as Step };
          save(n);
          setState(n);
        } else if (state.found && !['signal', 'done'].includes(state.step)) {
          const r = await activate.ticket(state.found.ticket).catch((e) => {
            if (e instanceof ActivateError && e.code === 'ticket-expired') return null;
            throw e;
          });
          if (!r) { forget(); setState(EMPTY); }
        }
      } catch (e) {
        fail(e);
      } finally {
        setChecking(false);
      }
    })();
  }, [token, state.found, state.step]);

  // Without the password in memory (after a reload), a new account has to be entered again.
  useEffect(() => {
    if (!checking && !user && !account && (state.step === 'vehicle' || state.step === 'confirm')) go({ step: 'account' });
  }, [checking, user, account, state.step]);

  const restart = () => { forget(); setAccount(null); setError(null); setState(EMPTY); navigate('/activate', { replace: true }); };

  if (checking || !config) {
    return <PublicFrame><div className="wizard-card">{error ? <ErrorBox error={error} onRestart={restart} /> : <p className="muted">Loading…</p>}</div></PublicFrame>;
  }
  if (!config.enabled) {
    return (
      <PublicFrame>
        <div className="wizard-card">
          <h1>Activation isn't open yet</h1>
          <p>Online activation of trackers isn't available yet. Please contact Fleet Functions to get your tracker set up.</p>
        </div>
      </PublicFrame>
    );
  }

  const { step, found } = state;
  const n = NUMBERED.indexOf(step);
  return (
    <PublicFrame>
      <div className="wizard-card">
        {n >= 0 && (
          <div className="wsteps" aria-label={`Step ${n + 1} of ${NUMBERED.length}`}>
            <span className="muted small">Step {n + 1} of {NUMBERED.length} · {STEP_LABEL[step]}</span>
            <div className="wsteps-bar">{NUMBERED.map((s, i) => <span key={s} className={i <= n ? 'on' : ''} />)}</div>
          </div>
        )}
        {found && n > 0 && <p className="muted small">Tracker {found.tracker.model ?? ''} · IMEI <span className="mono">{found.tracker.imei}</span></p>}
        {error && step === 'identify' && <ErrorBox error={error} onRestart={restart} />}

        {step === 'identify' && (
          <Identify onFound={(f) => { setError(null); go({ ...EMPTY, found: f, step: 'guide' }); }} onError={fail} />
        )}
        {step === 'guide' && found && (
          <>
            <h1>Fit your tracker</h1>
            <p className="muted">You can activate now and fit the tracker later, or fit it first.</p>
            <GuideContent model={found.tracker.model} />
            <p className="small"><Link to={`/guide${found.tracker.model ? `/${encodeURIComponent(found.tracker.model)}` : ''}`} target="_blank">Open the guide on its own page</Link></p>
            <Nav onNext={() => go({ step: 'account' })} next="Next: your account" />
          </>
        )}
        {step === 'account' && found && (
          <AccountStep orderEmail={found.orderEmail} account={account} minPassword={config.minPassword}
            onBack={() => go({ step: 'guide' })}
            onNext={(a) => { setAccount(a); go({ step: 'vehicle' }); }} />
        )}
        {step === 'vehicle' && (
          <VehicleStep vehicle={state.vehicle} onBack={() => go({ step: 'account' })} onNext={(v) => go({ vehicle: v, step: 'confirm' })} />
        )}
        {step === 'confirm' && found && state.vehicle && (
          <Confirm found={found} vehicle={state.vehicle} account={account} config={config}
            onBack={() => go({ step: 'vehicle' })}
            onEmailTaken={() => { setAccount(null); go({ step: 'account' }); }}
            onDone={async (r) => {
              await refresh();
              go({ found: null, step: 'signal', done: { vehicleId: r.vehicleId, expiresAt: r.expiresAt } });
              setAccount(null);
            }} />
        )}
        {step === 'signal' && state.done && <Waiting vehicleId={state.done.vehicleId} vehicleName={state.vehicle?.name ?? 'your vehicle'} onNext={() => go({ step: 'done' })} />}
        {step === 'done' && state.done && (
          <>
            <h1>All done ✓</h1>
            <p>Your tracker is active. Tracking is included until <strong>{longDate(state.done.expiresAt)}</strong>.</p>
            <p className="muted">If the tracker hasn't reported yet, {state.vehicle?.name ?? 'the vehicle'} appears on your map as soon as it does.</p>
            <div className="wnav">
              <button className="primary" onClick={() => { forget(); navigate('/', { replace: true }); }}>Open the live map</button>
            </div>
          </>
        )}
      </div>
    </PublicFrame>
  );
}

function ErrorBox({ error, onRestart }: { error: { message: string; code?: string }; onRestart: () => void }) {
  return (
    <div className="werror" role="alert">
      <p>{error.message}</p>
      {error.code === 'already-activated' && <p><Link to="/">Go to the login page</Link></p>}
      {error.code === 'ticket-expired' && <button className="link" onClick={onRestart}>Start again</button>}
    </div>
  );
}

function Nav({ onBack, onNext, next = 'Next', busy = false, nextDisabled = false, submit = false }: {
  onBack?: () => void; onNext?: () => void; next?: string; busy?: boolean; nextDisabled?: boolean; submit?: boolean;
}) {
  return (
    <div className="wnav">
      {onBack && <button type="button" className="link" onClick={onBack} disabled={busy}>← Back</button>}
      <button type={submit ? 'submit' : 'button'} className="primary" onClick={submit ? undefined : onNext} disabled={busy || nextDisabled}>{next}</button>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return <label className="wfield"><span>{label}</span>{children}{hint && <small className="muted">{hint}</small>}</label>;
}

function Identify({ onFound, onError }: { onFound: (f: Identified) => void; onError: (e: unknown) => void }) {
  const [imei, setImei] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try { onFound(await activate.byCode(imei, code)); } catch (err) { onError(err); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="wform">
      <h1>Activate your tracker</h1>
      <p className="muted">Scan the QR code on the label with your phone's camera, or type the IMEI and claim code printed on it.</p>
      <Field label="IMEI" hint="15 digits">
        <input type="text" inputMode="numeric" autoComplete="off" required value={imei} onChange={(e) => setImei(e.target.value)} />
      </Field>
      <Field label="Claim code" hint="8 letters and numbers, like ABCD-2345">
        <input type="text" autoCapitalize="characters" autoComplete="off" spellCheck={false} required value={code} onChange={(e) => setCode(e.target.value)} className="mono" />
      </Field>
      <Nav next={busy ? 'Checking…' : 'Continue'} busy={busy} submit />
    </form>
  );
}

function AccountStep({ orderEmail, account, minPassword, onBack, onNext }: {
  orderEmail: string | null; account: NewAccount | null; minPassword: number; onBack: () => void; onNext: (a: NewAccount | null) => void;
}) {
  const { user, login, logout } = useAuth();
  const [mode, setMode] = useState<'new' | 'login'>('new');
  const [a, setA] = useState<NewAccount>(account ?? { name: '', email: orderEmail ?? '', phone: '', company: '', password: '' });
  const [loginEmail, setLoginEmail] = useState(orderEmail ?? '');
  const [loginPw, setLoginPw] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k: keyof NewAccount) => (e: { target: { value: string } }) => setA({ ...a, [k]: e.target.value });

  if (user) {
    return (
      <>
        <h1>Your account</h1>
        {user.impersonatedBy ? (
          <p className="werror">You are viewing the app as {user.name}. Staff can't activate trackers for a customer; log out first.</p>
        ) : (
          <p>The tracker will be added to the account of <strong>{user.customerName}</strong> (logged in as {user.email}).</p>
        )}
        <p className="small"><button className="link" onClick={() => void logout()}>Use a different account</button></p>
        <Nav onBack={onBack} onNext={user.impersonatedBy ? undefined : () => onNext(null)} next="Next: your vehicle" nextDisabled={!!user.impersonatedBy} />
      </>
    );
  }
  const submitNew = (e: FormEvent) => {
    e.preventDefault();
    if (a.password.length < minPassword) { setError(`The password needs at least ${minPassword} characters.`); return; }
    onNext(a);
  };
  const submitLogin = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try { await login(loginEmail, loginPw); } catch (err) { setError((err as Error).message); }
    setBusy(false);
  };
  return (
    <>
      <h1>Your account</h1>
      <div className="wtabs" role="tablist">
        <button role="tab" aria-selected={mode === 'new'} className={mode === 'new' ? 'on' : ''} onClick={() => { setMode('new'); setError(''); }}>New account</button>
        <button role="tab" aria-selected={mode === 'login'} className={mode === 'login' ? 'on' : ''} onClick={() => { setMode('login'); setError(''); }}>I have an account</button>
      </div>
      {mode === 'new' ? (
        <form onSubmit={submitNew} className="wform">
          <Field label="Your name"><input type="text" autoComplete="name" required maxLength={100} value={a.name} onChange={set('name')} /></Field>
          <Field label="Email" hint="You log in with this."><input type="email" autoComplete="email" required maxLength={200} value={a.email} onChange={set('email')} /></Field>
          <Field label="Phone"><input type="tel" autoComplete="tel" maxLength={40} value={a.phone} onChange={set('phone')} /></Field>
          <Field label="Company (optional)"><input type="text" autoComplete="organization" maxLength={100} value={a.company} onChange={set('company')} /></Field>
          <Field label="Password" hint={`At least ${minPassword} characters.`}>
            <span className="pwrow">
              <input type={show ? 'text' : 'password'} autoComplete="new-password" required minLength={minPassword} maxLength={200} value={a.password} onChange={set('password')} />
              <button type="button" onClick={() => setShow(!show)}>{show ? 'Hide' : 'Show'}</button>
            </span>
          </Field>
          {error && <p className="error" role="alert">{error}</p>}
          <Nav onBack={onBack} next="Next: your vehicle" submit />
        </form>
      ) : (
        <form onSubmit={submitLogin} className="wform">
          <Field label="Email"><input type="email" autoComplete="username" required value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} /></Field>
          <Field label="Password"><input type="password" autoComplete="current-password" required value={loginPw} onChange={(e) => setLoginPw(e.target.value)} /></Field>
          {error && <p className="error" role="alert">{error}</p>}
          <Nav onBack={onBack} next={busy ? 'Logging in…' : 'Log in'} busy={busy} submit />
        </form>
      )}
    </>
  );
}

function VehicleStep({ vehicle, onBack, onNext }: { vehicle: NewVehicle | null; onBack: () => void; onNext: (v: NewVehicle) => void }) {
  const [v, setV] = useState<NewVehicle>(vehicle ?? { name: '', registration: '', type: 'car', make: '', model: '' });
  const set = (k: keyof NewVehicle) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });
  const submit = (e: FormEvent) => { e.preventDefault(); onNext(v); };
  return (
    <form onSubmit={submit} className="wform">
      <h1>Your vehicle</h1>
      <Field label="Name" hint="How it shows on your map, e.g. the registration or “Blue van”.">
        <input type="text" required maxLength={60} value={v.name} onChange={set('name')} />
      </Field>
      <Field label="Registration (optional)"><input type="text" maxLength={30} value={v.registration} onChange={set('registration')} /></Field>
      <Field label="Type">
        <select value={v.type} onChange={set('type')}>{TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      </Field>
      <div className="wpair">
        <Field label="Make (optional)"><input type="text" maxLength={60} value={v.make} onChange={set('make')} /></Field>
        <Field label="Model (optional)"><input type="text" maxLength={60} value={v.model} onChange={set('model')} /></Field>
      </div>
      <Nav onBack={onBack} next="Next: activate" submit />
    </form>
  );
}

function Confirm({ found, vehicle, account, config, onBack, onEmailTaken, onDone }: {
  found: Identified; vehicle: NewVehicle; account: NewAccount | null; config: ActivateConfig;
  onBack: () => void; onEmailTaken: () => void; onDone: (r: { vehicleId: number; expiresAt: string }) => Promise<void>;
}) {
  const { user } = useAuth();
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const strip = <T extends object>(o: T) => Object.fromEntries(Object.entries(o).filter(([, x]) => x !== '')) as T;
      const r = await activate.complete({
        ticket: found.ticket, acceptTerms: accept, termsVersion: config.termsVersion,
        vehicle: strip(vehicle), ...(user ? {} : { account: strip(account!) }),
      });
      await onDone(r);
    } catch (err) {
      if (err instanceof ActivateError && err.code === 'email-taken') { window.alert(err.message); onEmailTaken(); return; }
      setError((err as Error).message);
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="wform">
      <h1>Activate</h1>
      <dl className="wsummary">
        <dt>Tracker</dt><dd>{found.tracker.model ?? 'Tracker'} · <span className="mono">{found.tracker.imei}</span></dd>
        <dt>Vehicle</dt><dd>{vehicle.name}{vehicle.registration ? ` (${vehicle.registration})` : ''}</dd>
        <dt>Account</dt><dd>{user ? `${user.customerName} · ${user.email}` : `${account?.name} · ${account?.email}`}</dd>
        <dt>Tracking</dt><dd>{config.years === 1 ? 'One year' : `${config.years} years`} from today</dd>
      </dl>
      <label className="wcheck">
        <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} required />
        <span>I accept the <Link to="/terms" target="_blank">terms and privacy notice</Link>.</span>
      </label>
      {error && <p className="error" role="alert">{error}</p>}
      <Nav onBack={onBack} next={busy ? 'Activating…' : 'Activate tracker'} busy={busy} nextDisabled={!accept} submit />
    </form>
  );
}

function Waiting({ vehicleId, vehicleName, onNext }: { vehicleId: number; vehicleName: string; onNext: () => void }) {
  const [signal, setSignal] = useState<Signal | null>(null);
  const [error, setError] = useState('');
  const connected = !!signal?.firstDataAt;
  useEffect(() => {
    if (connected) return;
    let stop = false;
    const poll = async () => {
      try { setSignal(await activate.signal(vehicleId)); setError(''); } catch (e) { setError((e as Error).message); }
      if (!stop) timer = window.setTimeout(poll, 5000);
    };
    let timer = window.setTimeout(poll, 0);
    return () => { stop = true; window.clearTimeout(timer); };
  }, [vehicleId, connected]);
  const p = signal?.position;
  return (
    <>
      <h1>{connected ? 'Connected ✓' : 'Waiting for your tracker…'}</h1>
      {connected ? (
        <>
          <p>We're receiving data from {vehicleName}.</p>
          {p && (
            <div className="wmap">
              <FleetMap vehicles={[{ id: vehicleId, name: vehicleName, state: 'parked', lat: p.lat, lon: p.lon, course: null }]} selectedId={vehicleId} ready />
            </div>
          )}
        </>
      ) : (
        <>
          <div className="wpulse" aria-hidden="true" />
          <p>Make sure the ignition is on and the tracker has a view of the sky. The first data can take a few minutes.</p>
          <p className="muted small">You don't have to wait: {vehicleName} appears on your map as soon as the tracker reports.</p>
        </>
      )}
      {error && <p className="error small">{error}</p>}
      <div className="wnav"><button className={connected ? 'primary' : 'link'} onClick={onNext}>{connected ? 'Continue' : 'Finish now'}</button></div>
    </>
  );
}

